/* eslint-disable @typescript-eslint/no-explicit-any */
import { collectLibrary } from './lib';

/**
 * Signals delegated to FingerprintJS.
 *
 * WHY DELEGATE THESE:
 * These are the signals where naive implementations break. FingerprintJS has
 * years of battle-testing across browser versions, OS updates, and GPU driver
 * changes. Rolling our own for these would mean replicating hundreds of edge
 * case fixes. Specific reasons per signal:
 *
 * - canvas: The drawing commands must be carefully chosen to maximize cross-
 *   system divergence. FPJS uses a geometry canvas and a text canvas, handles
 *   Safari's canvas randomization (added in 15.4), normalizes for DPR
 *   differences, and avoids commands that produce identical output everywhere.
 *   A naive "draw a rectangle and toDataURL()" misses most of this.
 *
 * - audio: OfflineAudioContext rendering has timing-dependent quirks in Safari
 *   and Firefox. FPJS handles the baseLatency Infinity edge case (fixed in
 *   v4.6.2), the AudioContext constructor differences across browsers, and
 *   normalizes the oscillator → compressor → analyser pipeline output.
 *
 * - fonts: Side-channel font detection (measuring text width with fallback
 *   fonts) requires careful handling of: browser zoom levels, system font
 *   substitution, macOS font smoothing, and the fact that some browsers
 *   return identical measurements for similar fonts. FPJS tests against a
 *   curated list with known-good baseline measurements.
 *
 * - webgl: The WEBGL_debug_renderer_info extension is being deprecated in
 *   Chrome 110+. FPJS gracefully degrades by using alternative WebGL
 *   parameter combinations when unmasked renderer is unavailable. It also
 *   handles WebGL context loss, context creation failures, and extension
 *   enumeration order normalization.
 *
 * Additionally, FPJS collects some signals we don't need to rewrite:
 * - osCpu, languages, colorDepth, deviceMemory, screenResolution,
 *   hardwareConcurrency, timezone, sessionStorage, localStorage, indexedDB,
 *   colorGamut, invertedColors, forcedColors, monochrome, contrast,
 *   reducedMotion, reducedTransparency, hdr, math, pdfViewerEnabled,
 *   architecture, applePay, vendorFlavors, cookiesEnabled, domBlockers, etc.
 *
 * We extract ALL of these as raw values from result.components so we can
 * inspect them individually. We DON'T use FPJS's visitorId hash — we want
 * raw data for our own correlation engine.
 */

interface BrowserIdFingerprint {
  userAgent: string;
  language: string;
  languages: string[];
  cookieEnabled: boolean;
  doNotTrack: string | null;
  pdfViewerEnabled: boolean;
  webdriver: boolean;
}

interface PermissionsFingerprint {
  notifications: string | null;
  geolocation: string | null;
  camera: string | null;
  microphone: string | null;
}

// ============================================================================
// IN-HOUSE COLLECTORS
// ============================================================================
// These are signals where:
//   a) The API is straightforward (no browser quirks to handle), OR
//   b) FingerprintJS doesn't collect them at all, OR
//   c) We want our own raw representation for comparison/redundancy

/**
 * SCREEN PROPERTIES
 *
 * WHY IN-HOUSE (not delegated to FPJS):
 * FPJS collects screenResolution and colorDepth, but as a single tuple.
 * We want the full breakdown including availWidth/availHeight (which FPJS
 * doesn't collect — these differ from full resolution by taskbar/dock size)
 * and orientation type.
 *
 * JUSTIFICATION PER PROPERTY:
 * - width/height: Physical screen resolution tied to monitor hardware.
 *   Combined with pixelRatio, strongly identifies the display model.
 * - availWidth/availHeight: Differs by OS taskbar/dock size and position.
 *   A user with a bottom taskbar vs side taskbar vs auto-hide gives
 *   different values on the same monitor. FPJS DOES NOT collect this.
 * - colorDepth: Almost always 24, but 30-bit/48-bit displays exist and
 *   distinguish high-end setups.
 * - pixelRatio: Distinguishes HiDPI displays. A 1080p monitor at 100%
 *   scaling = 1.0; at 125% = 1.25. Highly correlated to OS+display combos.
 * - orientation: "landscape-primary" vs "portrait-primary" — useful for
 *   correlating mobile sessions.
 */
function collectScreen() {
  const { screen } = window;

  return {
    width: screen.width,
    height: screen.height,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
    colorDepth: screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
    orientation: screen.orientation?.type ?? null,
  };
}

/**
 * HARDWARE CHARACTERISTICS
 *
 * WHY IN-HOUSE:
 * FPJS collects hardwareConcurrency and deviceMemory, but not maxTouchPoints
 * or platform. These are one-liner APIs with zero browser quirks.
 *
 * JUSTIFICATION PER PROPERTY:
 * - cores: Logical CPU cores (2 to 32+). Combined with GPU, narrows down
 *   the hardware profile significantly.
 * - memory: Chrome-only, returns device RAM in bucketed GB (0.25, 0.5, 1,
 *   2, 4, 8). Low entropy alone but useful in combination.
 * - maxTouchPoints: 0 = no touchscreen, 1+ = touch-capable. Distinguishes
 *   laptop-with-touch from desktop. 10 is common for tablets.
 * - platform: Coarse OS identifier. Mostly redundant with UA but survives
 *   some UA spoofing scenarios.
 */
function collectHardware() {
  return {
    cores: navigator.hardwareConcurrency ?? null,
    memory: (navigator as any).deviceMemory ?? null,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    platform: navigator.platform ?? '',
  };
}

/**
 * BROWSER IDENTIFICATION PROPERTIES
 *
 * WHY IN-HOUSE:
 * FPJS collects languages and cookiesEnabled, but NOT the full set we want.
 * Specifically, FPJS doesn't collect doNotTrack or webdriver. These are
 * single-property reads with no quirks.
 *
 * JUSTIFICATION PER PROPERTY:
 * - userAgent: Redundant with server-side, but included so the client-side
 *   fingerprint is self-contained. ALSO lets us detect UA spoofing: if
 *   client-side UA ≠ server-side UA header, the user is tampering.
 * - language/languages: The full ordered language list is surprisingly unique.
 *   "en-CO,en,es-CO,es,en-GB,en-US" reflects a specific bilingual user's
 *   preferences. FPJS collects this too, but normalizes it differently.
 * - cookieEnabled: false is unusual and itself a signal.
 * - doNotTrack: Segments users into privacy-conscious vs default groups.
 *   FPJS DOES NOT collect this.
 * - pdfViewerEnabled: Most browsers have it enabled, but headless/automated
 *   browsers often don't. Good bot detection signal.
 * - webdriver: Directly indicates automation (Selenium, Puppeteer, Playwright).
 *   FPJS DOES NOT expose this directly.
 */
function collectBrowser(): BrowserIdFingerprint {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: [...navigator.languages],
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack ?? null,
    pdfViewerEnabled: navigator.pdfViewerEnabled ?? false,
    webdriver: navigator.webdriver ?? false,
  };
}

/**
 * TIMEZONE
 *
 * WHY IN-HOUSE:
 * FPJS collects the IANA timezone string but NOT the numeric offset. The
 * offset is useful for catching users who spoof the IANA name but forget to
 * change the system clock. Also, cross-referencing timezone with server-side
 * CloudFront-Viewer-Country catches VPN users (e.g., "America/New_York"
 * but country = CO).
 *
 * JUSTIFICATION PER PROPERTY:
 * - timezone (IANA): "America/Bogota" is more specific than the offset alone.
 *   Two users at UTC-5 could be in Bogota, Lima, New York, or Havana.
 * - timezoneOffset: Numeric check. Mismatch with IANA string = tampering.
 */
function collectTimezone() {
  let timezone = '';

  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = 'unknown';
  }

  return {
    timezone,
    timezoneOffset: new Date().getTimezoneOffset(),
  };
}

/**
 * NETWORK INFORMATION
 *
 * WHY IN-HOUSE:
 * FPJS DOES NOT collect Network Information API data at all. This is a
 * Chrome/Edge-only API, but since your lambda event shows Chrome users,
 * it's worth collecting.
 *
 * JUSTIFICATION PER PROPERTY:
 * - connectionType: "wifi" vs "4g" vs "ethernet" — segments mobile vs
 *   desktop and home vs cellular.
 * - downlink/rtt: Estimated bandwidth and latency. Varies by ISP and
 *   location — a user in Bogotá on Claro has different values than
 *   one on Movistar.
 * - saveData: Users who enable data saver are a small, identifiable group.
 */
function collectNetwork() {
  const connection = (navigator as any).connection;

  return {
    connectionType: connection?.effectiveType ?? null,
    downlink: connection?.downlink ?? null,
    rtt: connection?.rtt ?? null,
    saveData: connection?.saveData ?? null,
    detected: !!connection,
  };
}

/**
 * PERMISSIONS STATE
 *
 * WHY IN-HOUSE:
 * FPJS DOES NOT collect permission states. This is a behavioral fingerprint:
 * a user who has previously granted camera permission on your domain is
 * distinct from one who hasn't. If two "different" accounts both have
 * notifications=granted on the same browser, they share a browser profile.
 *
 * IMPORTANT: We query the state without triggering a permission prompt
 * (Permissions API allows this). We never request access.
 *
 * JUSTIFICATION PER PROPERTY:
 * - notifications/geolocation/camera/microphone: The state (granted/denied/
 *   prompt) reflects past user decisions. High signal for multi-account
 *   detection because permission grants are tied to the browser profile.
 */
async function collectPermissions(): Promise<PermissionsFingerprint> {
  const result: PermissionsFingerprint = {
    notifications: null,
    geolocation: null,
    camera: null,
    microphone: null,
  };

  if (!navigator.permissions?.query) return result;

  const queries: Array<{ key: keyof PermissionsFingerprint; name: string }> = [
    { key: 'notifications', name: 'notifications' },
    { key: 'geolocation', name: 'geolocation' },
    { key: 'camera', name: 'camera' },
    { key: 'microphone', name: 'microphone' },
  ];

  await Promise.allSettled(
    queries.map(async ({ key, name }) => {
      try {
        const status = await navigator.permissions.query({
          name: name as PermissionName,
        });
        result[key] = status.state;
      } catch {
        // Permission name not supported in this browser
      }
    }),
  );

  return result;
}

/**
 * MEDIA CODEC SUPPORT
 *
 * WHY IN-HOUSE:
 * FPJS DOES NOT collect codec support data. The set of supported video/audio
 * codecs depends on the browser, OS, and installed codec packs. A Windows
 * machine with K-Lite installed supports different formats than stock macOS.
 *
 * We use MediaSource.isTypeSupported() — synchronous and fast, no media
 * elements needed.
 *
 * JUSTIFICATION:
 * Distinguishes OS + browser combinations that otherwise look similar in
 * other signals. Particularly useful: H.265 support varies wildly (hardware
 * decode required on most platforms), and AV1 support segments by GPU gen.
 */
function collectMedia() {
  const videoCodecs = [
    'video/mp4; codecs="avc1.42E01E"', // H.264 Baseline
    'video/mp4; codecs="avc1.4D401E"', // H.264 Main
    'video/mp4; codecs="avc1.64001E"', // H.264 High
    'video/mp4; codecs="hvc1.1.6.L93.B0"', // H.265/HEVC
    'video/webm; codecs="vp8"', // VP8
    'video/webm; codecs="vp9"', // VP9
    'video/webm; codecs="av01.0.01M.08"', // AV1
    'video/ogg; codecs="theora"', // Theora
  ];

  const audioCodecs = [
    'audio/mp4; codecs="mp4a.40.2"', // AAC-LC
    'audio/mp4; codecs="mp4a.40.5"', // HE-AAC
    'audio/webm; codecs="opus"', // Opus
    'audio/webm; codecs="vorbis"', // Vorbis
    'audio/ogg; codecs="flac"', // FLAC
    'audio/mpeg', // MP3
  ];

  const checkSupport = (types: string[]): string[] => {
    if (typeof MediaSource === 'undefined') return [];

    return types.filter((type) => {
      try {
        return MediaSource.isTypeSupported(type);
      } catch {
        return false;
      }
    });
  };

  return {
    videoCodecs: checkSupport(videoCodecs),
    audioCodecs: checkSupport(audioCodecs),
  };
}

/**
 * STORAGE API AVAILABILITY
 *
 * WHY IN-HOUSE:
 * FPJS checks localStorage, sessionStorage, and indexedDB, but it only checks
 * existence (typeof), not actual write capability. Our version does a real
 * write/delete cycle, which catches cases where the API exists but is blocked
 * by policy (e.g., Safari ITP in some iframe contexts).
 *
 * JUSTIFICATION:
 * The combination of what's available vs blocked signals browser configuration
 * and privacy settings. We only check availability — never read/write data.
 */
function collectStorage() {
  const check = (fn: () => boolean): boolean => {
    try {
      return fn();
    } catch {
      return false;
    }
  };

  return {
    localStorageAvailable: check(() => {
      const key = '__fp_test__';
      localStorage.setItem(key, '1');
      localStorage.removeItem(key);
      return true;
    }),

    sessionStorageAvailable: check(() => {
      const key = '__fp_test__';
      sessionStorage.setItem(key, '1');
      sessionStorage.removeItem(key);
      return true;
    }),

    indexedDBAvailable: check(() => !!window.indexedDB),
  };
}

/**
 * MATH ENGINE FINGERPRINT
 *
 * WHY IN-HOUSE (even though FPJS also collects math):
 * FPJS collects a subset of math operations. We collect our own set with
 * specific inputs chosen for maximum divergence between V8 (Chrome),
 * SpiderMonkey (Firefox), and JavaScriptCore (Safari).
 *
 * Having BOTH FPJS's math values and ours lets us detect if someone has
 * patched one but not the other.
 *
 * JUSTIFICATION:
 * JS engines implement Math functions with different internal algorithms,
 * producing different floating-point results at precision limits. These are
 * deterministic per engine — they don't change between requests. They help
 * distinguish Chrome from Firefox from Safari even when the UA is spoofed.
 */
function collectMath() {
  return {
    tan: Math.tan(-1e300),
    atan2: Math.atan2(1, 0.5),
    acos: Math.acos(0.5),
    sinh: Math.sinh(1),
  };
}

// ============================================================================
// MAIN COLLECTOR
// ============================================================================

/**
 * Collects all fingerprint signals — library-backed and in-house — and
 * returns them as a single structured object.
 *
 * Call on login, signup, or other high-value actions. Not on every page load.
 *
 * Typical execution time: 100-400ms (FPJS audio + canvas are the bottleneck).
 * If FPJS is blocked by an ad-blocker, in-house collectors still run (~10ms).
 */
export async function collectFingerprint() {
  const [{ rootComponents, deviceId }, permissions] = await Promise.all([
    //
    collectLibrary(),
    collectPermissions(),
  ]);

  return {
    deviceId,
    rootComponents,

    screen: collectScreen(),
    hardware: collectHardware(),
    browser: collectBrowser(),
    timezone: collectTimezone(),
    network: collectNetwork(),
    permissions,
    media: collectMedia(),
    storage: collectStorage(),
    math: collectMath(),

    collectedAt: new Date().toISOString(),
  };
}

// ============================================================================
// DECISION LOG: WHY EACH SIGNAL IS WHERE IT IS
// ============================================================================
//
// ┌──────────────────────┬─────────────┬──────────────────────────────────────┐
// │ Signal               │ Owner       │ Reason                               │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Canvas               │ FPJS        │ Safari canvas randomization, DPR     │
// │                      │             │ normalization, GPU-specific draw cmds │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Audio                │ FPJS        │ baseLatency Infinity edge case,      │
// │                      │             │ AudioContext constructor diffs,       │
// │                      │             │ Safari/Firefox timing quirks          │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Fonts                │ FPJS        │ Zoom-level compensation, system font  │
// │                      │             │ substitution, macOS font smoothing,   │
// │                      │             │ curated test list with known baselines│
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ WebGL                │ FPJS        │ WEBGL_debug_renderer_info deprecation │
// │                      │             │ graceful degradation, context loss    │
// │                      │             │ handling, extension order normal'tion │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Screen               │ In-house    │ Trivial API. We want availWidth/      │
// │                      │             │ availHeight which FPJS doesn't collect│
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Hardware             │ In-house    │ One-liners. FPJS misses touchPoints   │
// │                      │             │ and platform.                         │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Browser ID           │ In-house    │ FPJS misses doNotTrack and webdriver. │
// │                      │             │ We also want raw UA for server-side   │
// │                      │             │ cross-check.                          │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Timezone             │ In-house    │ We want the numeric offset too (FPJS  │
// │                      │             │ only gives IANA string). Offset       │
// │                      │             │ catches spoofed IANA names.           │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Network Info         │ In-house    │ FPJS doesn't collect this at all.     │
// │                      │             │ Chrome-only but high value for ISP    │
// │                      │             │ correlation.                          │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Permissions          │ In-house    │ FPJS doesn't collect this at all.     │
// │                      │             │ Behavioral fingerprint — permission   │
// │                      │             │ grants are tied to browser profile.   │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Media Codecs         │ In-house    │ FPJS doesn't collect this at all.     │
// │                      │             │ Codec support varies by OS + GPU +    │
// │                      │             │ installed codec packs.                │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Storage              │ In-house    │ We do real write/delete cycles vs     │
// │                      │             │ FPJS's typeof checks. Catches Safari  │
// │                      │             │ ITP iframe policy blocks.             │
// ├──────────────────────┼─────────────┼──────────────────────────────────────┤
// │ Math                 │ Both        │ Intentional redundancy. Different     │
// │                      │             │ input sets. Detects selective patching.│
// └──────────────────────┴─────────────┴──────────────────────────────────────┘
//
//
// ============================================================================
// SIGNALS CONSIDERED BUT NOT INCLUDED
// ============================================================================
//
// 1. navigator.getBattery()
//    WHY EXCLUDED: Returns current battery level and charging state, which
//    change constantly. Extremely volatile — useless for cross-session
//    fingerprinting. Also deprecated/removed in Firefox and restricted in
//    Chrome. Not worth the API surface.
//
// 2. navigator.getGamepads()
//    WHY EXCLUDED: Only returns data while a gamepad is actively connected
//    and has had user interaction. null for 99%+ of web users. Zero entropy
//    for a SaaS platform.
//
// 3. navigator.mediaDevices.enumerateDevices()
//    WHY EXCLUDED: Would list connected cameras/microphones/speakers — HIGH
//    entropy because device IDs are unique per origin. However, requires a
//    user gesture or prior permission grant in modern browsers. Too intrusive
//    for passive fingerprinting. CONSIDER for Phase 2 if you already have
//    media permissions from another feature.
//
// 4. CSS @media queries (prefers-color-scheme, prefers-reduced-motion, etc.)
//    WHY EXCLUDED: Accessible via window.matchMedia(). Each gives ~1 bit of
//    entropy. FPJS already collects reducedMotion, invertedColors, forcedColors,
//    monochrome, contrast, hdr, and colorGamut — so this is covered by the
//    library layer. No need to duplicate.
//
// 5. Keyboard layout (navigator.keyboard.getLayoutMap())
//    WHY EXCLUDED: Could distinguish QWERTY/AZERTY/QWERTZ users. Requires
//    secure context + user gesture + limited browser support. Too intrusive.
//
// 6. Speech synthesis voices (speechSynthesis.getVoices())
//    WHY EXCLUDED: Returns installed TTS voices — HIGH entropy, varies by
//    OS and locale. Excluded because of quirky async loading (requires a
//    voiceschanged event listener with a timeout). WORTH ADDING in Phase 2.
//    Implementation: speechSynthesis.getVoices().map(v => `${v.name}|${v.lang}`)
//
// 7. WebRTC local IP detection
//    WHY EXCLUDED: Could expose local network IPs (192.168.x.x) via
//    RTCPeerConnection — very strong signal. But modern browsers have patched
//    this leak, and it's ethically questionable for passive fingerprinting.
//
// 8. navigator.plugins
//    WHY EXCLUDED: Modern browsers (Chrome 91+) return a frozen, hardcoded
//    list for all users. No longer provides any entropy.
//
// 9. Performance.now() timing precision
//    WHY EXCLUDED: Browsers intentionally jitter this now (Spectre mitigation).
//    Precision level has been largely standardized. No longer reliable.
//
// 10. Client Hints (sec-ch-ua-* headers)
//     WHY EXCLUDED from client-side: Already collected SERVER-SIDE from the
//     lambda event headers. The server fingerprint should own these signals.
//     Collecting them client-side too would be pure redundancy with no
//     tampering-detection benefit (they come from the same browser).
//
// ============================================================================
